import Image from "next/image";
import Link from "next/link";

import { Fragment, useMemo } from "react";

import format from "date-fns/format";
import parseJSON from "date-fns/parseJSON";
import numeral from "numeral";
import { twMerge } from "tailwind-merge";

import { faLinkedin } from "@fortawesome/free-brands-svg-icons/faLinkedin";
import { faTwitter } from "@fortawesome/free-brands-svg-icons/faTwitter";
import { faGlobe } from "@fortawesome/free-solid-svg-icons/faGlobe";

import CustomCard from "components/CustomCard";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import PremiumServiceButton from "components/PremiumServiceButton";
import { ProfileCardButtonsWithAuthentication } from "components/ProfileCardButtons";
import { useApi } from "hooks/useApi";
import useAuthentication, { managedEntitiesUrl } from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import { baseFetcher } from "utils/baseFetcher";
import { emberAppBaseUrl } from "utils/envVariables";
import { getExpertiesTags } from "utils/insight";
import { ranks } from "consts";

const ProfilePageHeader = ({ profile, isCompany }: any) => {
  const { mobile } = useMediaQuery();
  const { authenticated, account } = useAuthentication();

  const {
    id,
    type,
    slug,
    name,
    avatarUrl,
    companyName,
    company,
    areasOfExpertise,
    title,
    country,
    joinedInAt,
    publishedInsightsCount,
    insightViewsCount,
    followersCount,
    isInsightProvider,
    isInternalUser,
    insightProviderBadgeText,
    isCorporateOfficer,
  } = profile;
  const ipRank = ranks[profile.ipRank];

  const expertiesTags = getExpertiesTags(areasOfExpertise);

  const { data: managedEntities } = useApi(
    isCorporateOfficer ? managedEntitiesUrl(company?.id) : null,
    baseFetcher
  );

  const linkedinUrl = useMemo(() => {
    if (type === "companies") return profile.linkedIn;

    return company?.linkedIn;
  }, [company?.linkedIn, profile.linkedIn, type]);

  const twitterUrl = useMemo(() => {
    if (type === "companies") return profile.twitter;

    return company?.twitter;
  }, [company?.twitter, profile.twitter, type]);

  const website = useMemo(() => {
    if (type === "companies") return profile.website;

    return company?.website;
  }, [company?.website, profile.website, type]);

  return (
    <CustomCard
      className={twMerge(
        "p-0",
        "mx-[-5px] mt-[-10px] mb-[-10px] rounded-none",
        "md:mx-0 md:mt-0 md:mb-[15px] md:rounded-xl",
        "bg-gradient-to-br from-[#1F1F20] to-[#0D4567]",
        "md:bg-none",
        "text-white",
        "md:text-active-black"
      )}
    >
      <div className="">
        <div
          className={twMerge(
            "flex",
            "w-full bg-gradient-to-r from-[#163243] to-[#0D4567] text-right",
            "pr-[16px]",
            "md:pr-[24px] md:rounded-t-xl",
            "min-h-[33px] md:min-h-[55px]"
          )}
        >
          {(isInsightProvider || isCompany) && !isInternalUser && (
            <div
              className={twMerge(
                "text-inactive-gray text-[12px]",
                "border border-solid border-inactive-gray",
                "px-[4px] mt-[1px] pt-[3px] pb-[1px] md:pt-[4px]",
                "ml-[15px] my-[5px]",
                "md:mt-[14px] md:mb-[14px] md:ml-[204px]"
              )}
            >
              {isCompany
                ? "COMPANY"
                : insightProviderBadgeText?.toUpperCase?.()}
            </div>
          )}
          {ipRank?.isTopHalf ? (
            <span
              className={twMerge(
                "flex-1 text-right",
                "text-[#CBB472] font-black uppercase",
                "text-[14px] leading-[32px] mt-[2px] md:mt-0",
                "md:text-[20px] md:leading-[55px]"
              )}
            >
              {ipRank.text}
            </span>
          ) : null}
        </div>
        {/* ) : (
          <div
            className={twMerge(
              "w-full h-[32px] md:h-[8px] md:mb-[48px] rounded-t-xl",
              "bg-gradient-to-r from-[#163243] to-[#0D4567] text-right"
            )}
          ></div>
        )} */}
        <div className={twMerge("md:pl-[36px] md:pr-[24px]")}>
          <div
            className={twMerge(
              "absolute shadow-lg",
              "border-solid border border-gray-200",
              type === "companies" ? "rounded-xl" : "rounded-full",
              "w-[108px] h-[108px] mt-[-22px] left-0 right-0 mx-auto",
              "md:w-[148px] md:h-[148px] md:mt-[-38px] md:left-auto md:right-auto md:mx-0"
            )}
          >
            <Image
              className={twMerge(
                type === "companies" ? "rounded-xl" : "rounded-full "
              )}
              src={avatarUrl?.xlarge}
              width={mobile ? 106 : 146}
              height={mobile ? 106 : 146}
              alt={name}
            />
          </div>
          <div
            className={twMerge(
              "w-full",
              "pt-[105px]",
              "md:pl-[170px] md:pt-[23px]"
            )}
          >
            <div className={twMerge("flex", "flex-col", "md:flex-row")}>
              <h1 className="flex-1 leading-[1.5]">
                <div
                  className={twMerge(
                    "md:text-active-black",
                    "text-[26px] text-center",
                    "md:text-[36px] md:text-left"
                  )}
                >
                  {name}
                </div>

                <div
                  className={twMerge(
                    "flex",
                    "flex-col text-center",
                    "md:flex-row md:text-left md:space-x-2 md:min-h-[35px]"
                  )}
                >
                  {!!title && (
                    <div
                      className={twMerge(
                        "text-[14px] font-normal",
                        "md:text-[18px] md:font-medium md:text-[#555]",
                        company && "md:truncate"
                      )}
                    >
                      {title}
                    </div>
                  )}
                  {!!title && !!company && (
                    <span className="text-[22px] text-[#555] leading-[27px] hidden md:inline">
                      {" "}
                      •{" "}
                    </span>
                  )}
                  {!!company && (
                    <Link
                      href={
                        isCorporateOfficer
                          ? `/entities/${managedEntities?.data?.[0]?.slug}`
                          : `/companies/${company.slug}`
                      }
                      className={twMerge(
                        " text-smart-teal cursor-pointer",
                        "text-[14px] font-normal",
                        "md:text-[18px] md:font-medium"
                      )}
                    >
                      {companyName}
                    </Link>
                  )}
                </div>
              </h1>

              <div className="text-right flex-col hidden md:flex">
                <div className={twMerge("mr-[-6px] mb-[5px] flex-1")}>
                  {id === account?.id ? (
                    <>
                      <a
                        className={twMerge(
                          "rounded-full px-[1.25em] py-[0.75em]",
                          "border-none bg-smart-teal hover:bg-[#1d8988] text-[13px] text-white hover:text-white"
                        )}
                        href={`${emberAppBaseUrl}/settings/profile`}
                      >
                        Edit My Profile
                      </a>
                    </>
                  ) : (
                    <div>
                      <ProfileCardButtonsWithAuthentication
                        slug={slug}
                        showIcon
                        hideTextOnActive
                      />
                    </div>
                  )}
                </div>
                <div className="md:text-[14px] md:mt-[10px]">
                  {country && (
                    <div>
                      <span className="font-bold">Location:</span> {country}
                    </div>
                  )}
                  {joinedInAt && (
                    <div suppressHydrationWarning>
                      <span className="font-bold">Joined:</span>{" "}
                      {format(parseJSON(joinedInAt), "dd MMMM yyyy")}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <hr className="bg-gray-400 hidden md:block" />

          <div
            className={twMerge(
              "flex items-center",
              "flex-col",
              "md:flex-row md:pb-[13px] md:space-x-[40px]"
            )}
          >
            {isInsightProvider && (
              <div
                className={twMerge(
                  "flex space-x-[38px]",
                  "my-[20px]",
                  "md:my-0"
                )}
              >
                <div className="text-center min-w-[55px]">
                  <div className="text-[16px] font-black">
                    {publishedInsightsCount > 999
                      ? numeral(publishedInsightsCount).format("0.0a")
                      : publishedInsightsCount}
                  </div>
                  <div className="text-[11px]">Insights</div>
                </div>
                <div className="text-center min-w-[55px]">
                  <div className="text-[16px] font-black">
                    {insightViewsCount > 999
                      ? numeral(insightViewsCount).format("0.0a")
                      : insightViewsCount}
                  </div>
                  <div className="text-[11px]">Views</div>
                </div>
                <div className="text-center min-w-[55px]">
                  <div className="text-[16px] font-black">
                    {followersCount > 999
                      ? numeral(followersCount).format("0.0a")
                      : followersCount}
                  </div>
                  <div className="text-[11px]">Followers</div>
                </div>
              </div>
            )}

            {isInsightProvider && expertiesTags?.length && (
              <div
                className={twMerge(
                  "flex-1 flex space-x-2 flex-wrap",
                  "text-[12px] mb-[24px] justify-center",
                  "md:text-[15px] md:text-[#707070] md:mb-0 md:justify-start",
                  "px-[15px] md:px-0"
                )}
              >
                {expertiesTags?.map?.((tag: string, index: number) => {
                  return (
                    <Fragment key={tag}>
                      <span className="first-of-type:list-none">{tag}</span>
                      {index !== expertiesTags.length - 1 ? (
                        <span className="text-[20px] md:text-[22.5px] leading-[1]">
                          {" "}
                          •{" "}
                        </span>
                      ) : (
                        ""
                      )}
                    </Fragment>
                  );
                })}
              </div>
            )}

            <div className="hidden md:flex space-x-[25px]">
              {linkedinUrl && (
                <Link href={linkedinUrl} rel="nofollow" target="_blank">
                  <FontAwesomeIcon
                    icon={faLinkedin}
                    className="text-[21px] text-[#7b8895]"
                  />
                </Link>
              )}
              {twitterUrl && (
                <Link
                  href={`https://twitter.com/${twitterUrl}`}
                  rel="nofollow"
                  target="_blank"
                >
                  <FontAwesomeIcon
                    icon={faTwitter}
                    className="text-[21px] text-[#7b8895]"
                  />
                </Link>
              )}
              {website && (
                <Link href={website} rel="nofollow" target="_blank">
                  <FontAwesomeIcon
                    icon={faGlobe}
                    className="text-[21px] text-[#7b8895]"
                  />
                </Link>
              )}
            </div>

            <div className={twMerge("md:hidden", "mb-[20px] flex")}>
              {id === account?.id ? (
                <>
                  <a
                    className={twMerge(
                      "rounded-full px-[1.25em] py-[0.75em]",
                      "border-none bg-smart-teal hover:bg-[#1d8988] text-[13px] text-white hover:text-white"
                    )}
                    href={`${emberAppBaseUrl}/settings/profile`}
                  >
                    Edit My Profile
                  </a>
                </>
              ) : (
                <ProfileCardButtonsWithAuthentication slug={slug} />
              )}
              {authenticated && profile?.hasPremiumServices && (
                <div className="ml-[10px]">
                  <PremiumServiceButton
                    profile={profile}
                    className={twMerge(
                      "text-[13px] text-white leading-[1]",
                      "rounded-full border border-solid border-white",
                      "bg-transparent",
                      "px-[1.25em] py-[0.75em]",
                      "hidden" // TODO: enable again later
                    )}
                  >
                    Premium Subscriptions
                  </PremiumServiceButton>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </CustomCard>
  );
};

export default ProfilePageHeader;
